import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Flex, Text, Spinner } from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';
import CustomDownloadQrIcon from '../../../../theme/icons/CustomDownloadQrIcon';
import { LOGO_BASE64 } from '../../../../utils/base64';

const QR_GENERATION_DELAY_MS = 700;
const QR_SIZE = 150;
const QR_LOGO_HEIGHT = 20;
const QR_LOGO_WIDTH = 65;
const DOWNLOAD_FILENAME = 'PassEntry QR Code.svg';

const FormQrCode = ({ value }: { value: string }) => {
  const [isGenerating, setIsGenerating] = useState(true);
  const qrContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsGenerating(true);
    const timer = setTimeout(() => {
      setIsGenerating(false);
    }, QR_GENERATION_DELAY_MS);
    return () => clearTimeout(timer);
  }, [value]);

  const handleDownload = useCallback(() => {
    const svg = qrContainerRef.current?.querySelector('svg');
    if (!svg) return;

    const svgString = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = DOWNLOAD_FILENAME;
    link.click();

    URL.revokeObjectURL(url);
  }, []);

  if (isGenerating) {
    return (
      <Flex direction="column" gap={6} alignItems="center" w="100%">
        <Text textStyle="bodyFamilyMedium" fontSize="16px">
          Creating your QR code...
        </Text>
        <Spinner size="lg" color="orangePrimary" />
        <Text textStyle="bodyFamilyRegular" fontSize="14px" textAlign="center">
          Just a moment while we get this ready for you.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={8} alignItems="center" w="100%">
      <Text textStyle="bodyFamilyRegular" fontSize="14px">
        This QR code links to your form. Download it to test and share.
      </Text>
      <div ref={qrContainerRef}>
        <QRCodeSVG
          value={value}
          size={QR_SIZE}
          level="Q"
          imageSettings={{
            src: LOGO_BASE64,
            height: QR_LOGO_HEIGHT,
            width: QR_LOGO_WIDTH,
            excavate: true,
          }}
        />
      </div>
      <Flex direction="row" w="100%" justifyContent="center">
        <Button
          variant="qrCodeAction"
          size="sm"
          onClick={handleDownload}
          w="200px"
        >
          <CustomDownloadQrIcon boxSize="18px" color="#4C4C4C" />
          <Text
            textStyle="bodyFamilyRegular"
            fontSize="14px"
            ml={2}
            color="#4C4C4C"
          >
            Download
          </Text>
        </Button>
      </Flex>
    </Flex>
  );
};

export default FormQrCode;
